import React from 'react';

import { Center, Flex, Loader, Progress, Space, Text, Tooltip } from '@mantine/core';
import { MIME_TYPES } from '@mantine/dropzone';
import { IconAlertTriangle, IconCheck, IconChecks, IconFileInvoice, IconNotebookOff, IconX } from '@tabler/icons-react';
import { AxiosError, HttpStatusCode } from 'axios';
import { formatDuration, intervalToDuration } from 'date-fns';
import { BrowserIcon } from 'hugeicons-react';
import i18next, { t } from 'i18next';
import { GoogleDriveIcon } from 'icons/GoogleDriveIcon';
import { PdfResourceIcon } from 'icons/PdfResourceIcon';
import { ShopifyIcon } from 'icons/ShopifyIcon';
import { VideoResourceIcon } from 'icons/VideoResourceIcon';
import { YoutubeResourceIcon } from 'icons/YoutubeResourceIcon';
import { NavigateFunction } from 'react-router';
import { LanguageService } from 'services/language-service';
import { NotificationService } from 'services/notification-service';
import { CRUDOperation } from 'shared/constants/crud-operation';
import { languageToDateFnsLocale } from 'shared/constants/languages';
import { RESOURCE_DELETE_NOTIF_ID, RESOURCE_UPDATE_NOTIF_ID } from 'shared/constants/notification-ids';
import { ROUTE_LINKS } from 'shared/constants/route-links';
import { DeleteElementConflictDto } from 'shared/interfaces/General';
import {
  Resource,
  ResourceAiProcessingStatus,
  ResourceType,
  UnUploadedResource,
  UploadResourceProgressEvent
} from 'shared/interfaces/Resource';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';

export const formatResourceName = (
  fileNameWithExtension: string,
  resourceType: ResourceType,
  includePath = false
): string => {
  if (resourceType === ResourceType.YOUTUBE) return fileNameWithExtension;
  if (resourceType === ResourceType.WEB) {
    try {
      const url = new URL(fileNameWithExtension);
      const baseUrl = url.hostname.startsWith('www.') ? url.hostname.slice(4) : url.hostname;
      const formattedUrl = includePath
        ? baseUrl + (url.pathname.endsWith('/') ? url.pathname.slice(0, -1) : url.pathname)
        : baseUrl.split('.')[0];
      return formattedUrl;
    } catch {
      return fileNameWithExtension;
    }
  }
  const fileNameSplit = fileNameWithExtension.split('.');
  if (resourceType === ResourceType.PDF || resourceType === ResourceType.VIDEO) {
    fileNameSplit.splice(fileNameSplit.length - 1);
  }
  return fileNameSplit.join('.');
};

export const getResourceIcon = (resourceType: ResourceType, w = 20, h = 20) => {
  switch (resourceType) {
    case ResourceType.VIDEO:
      return <VideoResourceIcon w={w} h={h} />;
    case ResourceType.TEXT:
      return <IconFileInvoice style={{ width: w, height: h, color: '#A349A4' }} />;
    case ResourceType.PDF:
      return <PdfResourceIcon w={w} h={h} />;
    case ResourceType.YOUTUBE:
      return <YoutubeResourceIcon w={w} h={h} />;
    case ResourceType.GOOGLE_DRIVE:
      return <GoogleDriveIcon w={w} h={w} />;
    case ResourceType.WEB:
      return <BrowserIcon size={w} />;
    case ResourceType.SHOPIFY_STORE:
      return <ShopifyIcon w={w} h={h} />;
    default:
      return;
  }
};

export const openResource = (resource: Resource, navigate: NavigateFunction, startTime = 0, page = 1) => {
  switch (resource.type) {
    case ResourceType.VIDEO:
      navigate(`${ROUTE_LINKS.videoViewer}/${resource._id}`, { state: { startTime } });
      break;
    case ResourceType.TEXT:
      navigate(`${ROUTE_LINKS.smartEditor}/${resource._id}`);
      break;
    case ResourceType.PDF:
      navigate(`${ROUTE_LINKS.pdfViewer}/${resource._id}`, { state: { page } });
      break;
    case ResourceType.YOUTUBE:
      navigate(`${ROUTE_LINKS.youtubeViewer}/${resource._id}`, { state: { startTime } });
      break;
    case ResourceType.GOOGLE_DRIVE:
      navigate(`${ROUTE_LINKS.googleDriveViewer}/${resource._id}`);
      break;
    case ResourceType.WEB:
      navigate(`${ROUTE_LINKS.websiteViewer}/${resource._id}`);
      break;
    case ResourceType.SHOPIFY_STORE:
      navigate(`${ROUTE_LINKS.stores}?store=${resource.shopifyResourceConfig?.store}`);
      break;
    default:
      break;
  }
};

export const getResourcesAsSelectItems = (resources: Resource[]) => {
  return resources.map((resource) => ({
    label: formatResourceName(resource.fileName, resource.type, !!resource.website?.subLinks),
    value: resource._id,
    iconComponent: getResourceIcon(resource.type, 20, 20)
  }));
};

export const MIME_TYPE_TO_RESSOURCE_TYPE = new Map([
  [MIME_TYPES.mp4, ResourceType.VIDEO],
  [MIME_TYPES.pdf, ResourceType.PDF],
  ['application/json', ResourceType.TEXT]
]);

export const getResourceAiProcessingStatusIndicator = (resource: Resource | UnUploadedResource) => {
  if (!('_id' in resource)) return null;

  switch (resource.aiProcessingStatus) {
    case ResourceAiProcessingStatus.IN_PROGRESS:
      return (
        <Tooltip
          label={
            resource.type === ResourceType.SHOPIFY_STORE
              ? t('RESOURCE_TILE.AI_PROCESSING_STATUS.SHOPIFY_IN_PROGRESS')
              : t('RESOURCE_TILE.AI_PROCESSING_STATUS.IN_PROGRESS')
          }
          position="top">
          <Center>
            <Loader m={0} p={0} size={19} style={{ cursor: 'pointer' }} />
          </Center>
        </Tooltip>
      );
    case ResourceAiProcessingStatus.FAILED:
      return (
        <Tooltip label={t('RESOURCE_TILE.AI_PROCESSING_STATUS.FAILED')} position="top">
          <IconAlertTriangle color="red" size={20} />
        </Tooltip>
      );
    case ResourceAiProcessingStatus.FAILED_NO_TEXT_FOUND:
      return (
        <Tooltip label={t('RESOURCE_TILE.AI_PROCESSING_STATUS.FAILED_NO_TEXT_FOUND')} position="top">
          <IconNotebookOff color="orange" size={20} />
        </Tooltip>
      );
    case ResourceAiProcessingStatus.DONE:
      return (
        <Tooltip label={t('RESOURCE_TILE.AI_PROCESSING_STATUS.DONE')} position="top">
          <IconChecks color="green" size={20} />
        </Tooltip>
      );
    default:
      return <></>;
  }
};

export const getResourceUploadProgress = (event: UploadResourceProgressEvent, key?: number) => {
  return (
    <Flex key={key} my={20} w="100%" direction="row" align="center" justify="space-between">
      <Flex direction="row" align="center">
        {getResourceIcon(event.resourceType, 18, 18)}
        <Space w={10} />
        <Text truncate maw={130}>
          {event.resourceFileName}
        </Text>
      </Flex>
      {event.failed ? (
        <Flex direction="row" align="center">
          <IconX style={{ width: 18, height: 18 }} color="red" />
          <Space w={10} />
          <Text w={200}>{i18next.t('FEED_BACK.FAILURE')}</Text>
        </Flex>
      ) : (
        <Flex direction="row" align="center">
          {event.progress >= 100 ? <IconCheck style={{ width: 18, height: 18 }} color="green" /> : <Loader size={18} />}
          <Space w={10} />
          <Progress
            w={200}
            h={3}
            color={event.progress >= 100 ? 'green' : 'black'}
            value={event.progress}
            animate={event.progress < 100}
          />
        </Flex>
      )}
    </Flex>
  );
};

export const isUplodedResource = (resource: Resource | UnUploadedResource): resource is Resource => {
  return '_id' in resource;
};

export const isUplodedResources = (resources?: Resource[] | UnUploadedResource[]): resources is Resource[] => {
  if (!resources) return false;
  resources.forEach((resource) => {
    if (!('_id' in resource)) return false;
  });
  return true;
};

export const isSyncResourceType = (resourceType: ResourceType): boolean => {
  return [ResourceType.GOOGLE_DRIVE, ResourceType.WEB].includes(resourceType);
};

export const isSyncEnabled = (resource: UnUploadedResource | Resource): boolean => {
  return !!(
    (('syncFrequency' in resource && resource.syncFrequency) ||
      ('syncFrequencyMs' in resource && resource.syncFrequencyMs)) &&
    isSyncResourceType(resource.type)
  );
};

export const formatMillisecondsToDuration = (ms: number) => {
  const duration = intervalToDuration({ start: 0, end: ms });
  return formatDuration(duration, { locale: languageToDateFnsLocale[LanguageService.getCurrentLanguage()] });
};

export const handleResourceOperationError = (
  error: AxiosError,
  operation: CRUDOperation,
  setConflictedSmartAssistants?: (smartAssistants: SmartAssistant[]) => void,
  openErrorModal?: () => void
) => {
  if (!error.response?.status) return;
  let failureKey = '';

  switch (operation) {
    case CRUDOperation.DELETE:
      if (error.response?.status == HttpStatusCode.Conflict) {
        const data = error.response?.data as DeleteElementConflictDto;
        const conflictedSmartAssistants = data.smartAssistants;

        if (conflictedSmartAssistants && conflictedSmartAssistants.length > 0)
          setConflictedSmartAssistants && setConflictedSmartAssistants(conflictedSmartAssistants);

        openErrorModal && openErrorModal();

        NotificationService.endAction(
          false,
          RESOURCE_DELETE_NOTIF_ID,
          'FEED_BACK.RESOURCES.FAILURE.DELETE.CONFLICT',
          error
        );
        return;
      }

      failureKey =
        error.response?.status == HttpStatusCode.NotFound
          ? 'FEED_BACK.RESOURCES.FAILURE.DELETE.NOT_FOUND'
          : 'FEED_BACK.RESOURCES.FAILURE.DELETE.UNKNOWN';
      NotificationService.endAction(false, RESOURCE_DELETE_NOTIF_ID, failureKey, error);
      break;
    case CRUDOperation.UPDATE:
      failureKey =
        error.response?.status == HttpStatusCode.NotFound
          ? 'FEED_BACK.RESOURCES.FAILURE.UPDATE.NOT_FOUND'
          : 'FEED_BACK.RESOURCES.FAILURE.UPDATE.UNKNOWN';
      NotificationService.endAction(false, RESOURCE_UPDATE_NOTIF_ID, failureKey, error);
      break;
    default:
      break;
  }
};
